import { useEffect, useLayoutEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useActiveMembership } from '@/features/session/useActiveMembership';
import { useTourStore } from './store';
import { TOURS } from './tours';
import { visibleSteps } from './types';

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getRect(el: Element): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function TourOverlay() {
  const { t } = useTranslation('tour');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { activeTourId, stepIndex, nextStep, prevStep, endTour } = useTourStore();
  const [rect, setRect] = useState<Rect | null>(null);
  const [notFound, setNotFound] = useState(false);

  const { membership } = useActiveMembership();
  const tour = TOURS.find((t2) => t2.id === activeTourId) ?? null;
  const steps = tour ? visibleSteps(tour, membership?.role) : [];
  const step = steps[stepIndex] ?? null;

  useEffect(() => {
    if (!step) return;
    if (!step.route) return;
    const onRoute = pathname === step.route || (step.route !== '/' && pathname.startsWith(`${step.route}/`));
    if (!onRoute) navigate(step.route);
  }, [step, pathname, navigate]);

  // Layout effect, and resolved synchronously when the anchor is already
  // mounted: clearing the rect first and waiting on the poll dropped the card
  // to the centre of the screen for a beat before it snapped onto the element.
  // When the anchor is not mounted yet (a step that changes route), the
  // previous step's rect is kept until the new one resolves rather than
  // falling back to centre.
  useLayoutEffect(() => {
    if (!step || !step.anchor) {
      setRect(null);
      setNotFound(false);
      return;
    }
    setNotFound(false);
    const spotlight = (el: Element) => {
      el.scrollIntoView({ block: 'nearest' });
      setRect(getRect(el));
    };
    const mounted = document.querySelector(`[data-tour="${step.anchor}"]`);
    if (mounted) {
      spotlight(mounted);
      return;
    }
    let attempts = 0;
    const maxAttempts = 20;
    const interval = setInterval(() => {
      attempts += 1;
      const el = document.querySelector(`[data-tour="${step.anchor}"]`);
      if (el) {
        clearInterval(interval);
        spotlight(el);
      } else if (attempts >= maxAttempts) {
        clearInterval(interval);
        setRect(null);
        setNotFound(true);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [step, pathname]);

  useEffect(() => {
    if (!step?.anchor) return;
    function recompute() {
      const el = document.querySelector(`[data-tour="${step?.anchor}"]`);
      if (el) setRect(getRect(el));
    }
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, { capture: true });
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, { capture: true });
    };
  }, [step]);

  useEffect(() => {
    if (!tour) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') endTour(true);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [tour, endTour]);

  if (!tour || !step) return null;

  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;
  const showSpotlight = !!step.anchor && !notFound && rect;

  let cardStyle: React.CSSProperties = {};
  const cardWidth = 320;
  if (showSpotlight && rect) {
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    const top = spaceBelow > 200 ? rect.top + rect.height + 12 : Math.max(12, rect.top - 12 - 220);
    let left = rect.left;
    left = Math.min(left, window.innerWidth - cardWidth - 12);
    left = Math.max(12, left);
    cardStyle = { position: 'fixed', top, left, width: cardWidth };
  } else {
    cardStyle = { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: cardWidth };
  }

  return (
    <div className="fixed inset-0 z-[100]">
      <div
        className="absolute inset-0 bg-black/55"
        style={
          showSpotlight && rect
            ? {
                clipPath: `polygon(0% 0%, 0% 100%, ${rect.left}px 100%, ${rect.left}px ${rect.top}px, ${rect.left + rect.width}px ${rect.top}px, ${rect.left + rect.width}px ${rect.top + rect.height}px, ${rect.left}px ${rect.top + rect.height}px, ${rect.left}px 100%, 100% 100%, 100% 0%)`,
              }
            : undefined
        }
      />
      {showSpotlight && rect ? (
        <div
          className="pointer-events-none absolute border-2 border-accent"
          style={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }}
        />
      ) : null}
      <div style={cardStyle} className="border border-border bg-surface p-4 shadow-md">
        <h3 className="font-display text-sm font-bold uppercase tracking-[.05em] text-ink">{t(step.titleKey)}</h3>
        <p className="mt-2 text-sm text-muted">{t(step.bodyKey)}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-mono text-[11px] text-muted">
            {t('controls.stepOf', { current: stepIndex + 1, total: steps.length })}
          </span>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => endTour(true)} className="text-sm font-medium text-muted hover:text-ink">
              {t('controls.skip')}
            </button>
            {!isFirst ? (
              <button type="button" onClick={prevStep} className="border border-border px-3 py-1.5 text-sm font-medium text-ink hover:bg-surface-2">
                {t('controls.back')}
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => (isLast ? endTour(true) : nextStep())}
              className="bg-accent px-3 py-1.5 text-sm font-bold text-on-accent hover:bg-accent-hover"
            >
              {isLast ? t('controls.done') : t('controls.next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
